import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { http, formatErr } from "../../lib/api";
import { usePolling } from "../../lib/usePolling";
import { useOpenParam } from "../../lib/useOpenParam";
import { formatDateTimeIT } from "../../lib/labels";
import { ClientSelect } from "../../components/ClientSelect";
import { toast } from "sonner";
import { Plus, FileText } from "lucide-react";

export const STAGE_LABEL = {
  nuovo_contatto: "Nuovo contatto", da_contattare: "Da contattare", contattato: "Contattato", qualificato: "Qualificato",
  demo_fissata: "Demo fissata", demo_effettuata: "Demo effettuata", offerta_inviata: "Preventivo inviato",
  negoziazione: "Negoziazione", accettata: "Accettata", vinta: "Vinta", persa: "Persa", sospesa: "Sospesa",
};
const EMPTY = { title: "", contact_id: null, salon_name: "", value_proposed: "", notes: "" };
const inp = "w-full px-3 py-2 rounded-md border";
const bs = { borderColor: "var(--border-silver)" };

export default function CrmTrattative() {
  const [agents, setAgents] = useState([]);
  const [agentId, setAgentId] = useState("");
  const [deals, setDeals] = useState([]);
  const [detail, setDetail] = useState(null);
  const [form, setForm] = useState(null);

  const loadAgents = async () => { const { data } = await http.get("/crm/agents"); setAgents(data); };
  const loadDeals = async () => { const { data } = await http.get("/crm/deals", { params: agentId ? { agent_id: agentId } : {} }); setDeals(data); };
  useEffect(() => { loadAgents().catch(() => {}); }, []);
  useEffect(() => {
    loadDeals().catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agentId]);
  usePolling(loadDeals, { interval: 10000, enabled: true });

  const openDeal = async (d) => { try { const { data } = await http.get(`/crm/deals/${d.id}`); setDetail(data); } catch (e) { toast.error(formatErr(e)); } };
  const openId = useOpenParam(deals, openDeal);
  const agentName = (id) => { if (!id) return "Diretta (admin)"; const a = agents.find((x) => x.id === id); return a ? (a.profile?.first_name || a.email) : "agente non più presente"; };

  const create = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.contact_id) return toast.error("Titolo e cliente richiesti.");
    try {
      const { data } = await http.post("/crm/deals", { title: form.title, contact_id: form.contact_id, value_proposed: form.value_proposed ? parseFloat(form.value_proposed) : null, notes: form.notes || null });
      setForm(null); await loadDeals(); await openDeal(data); toast.success("Trattativa diretta creata.");
    } catch (err) { toast.error(formatErr(err)); }
  };
  const setStage = async (stage) => {
    const loss_reason = ["persa", "sospesa"].includes(stage) ? window.prompt("Motivo (obbligatorio):") : null;
    if (["persa", "sospesa"].includes(stage) && !loss_reason) return;
    try { await http.put(`/crm/deals/${detail.deal.id}/stage`, { stage, loss_reason }); await openDeal(detail.deal); await loadDeals(); } catch (e) { toast.error(formatErr(e)); }
  };

  return (
    <div data-testid="crm-trattative-page" className="max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-2 flex-wrap gap-3">
        <h1 className="font-serif text-3xl">Trattative</h1>
        <div className="flex gap-2 flex-wrap">
          <select value={agentId} onChange={(e) => setAgentId(e.target.value)} data-testid="crm-trattative-agent" className="px-3 py-2 rounded-md border text-sm" style={bs}>
            <option value="">Tutte (dirette e agenti)</option>
            {agents.map((a) => <option key={a.id} value={a.id}>{a.profile?.first_name || a.email}</option>)}
          </select>
          <button onClick={() => setForm(form ? null : EMPTY)} data-testid="crm-deal-new" className="px-4 py-2 rounded-md text-white text-sm flex items-center gap-2" style={{ background: "var(--brand-dark)" }}><Plus className="w-4 h-4" /> Nuova trattativa diretta</button>
        </div>
      </div>
      <p className="text-sm text-neutral-500 mb-5">Percorso cliente diretto (senza agente né provvigioni): trattativa → preventivo PDF (Offerte) → vendita → pagamenti → progetto → consegna. Le trattative degli agenti sono gestite dagli agenti.</p>

      {form && (
        <form onSubmit={create} className="card-clean p-5 mb-5 grid grid-cols-1 md:grid-cols-2 gap-3 text-sm" data-testid="crm-deal-form">
          <label className="block"><span className="text-[10px] uppercase tracking-widest text-neutral-500 block mb-1">Titolo *</span><input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="cdl-title" className={inp} style={bs} /></label>
          <div className="block"><span className="text-[10px] uppercase tracking-widest text-neutral-500 block mb-1">Cliente *</span><ClientSelect testid="cdl-client" value={{ contact_id: form.contact_id, salon_name: form.salon_name }} onChange={(v) => setForm({ ...form, contact_id: v.contact_id, salon_name: v.salon_name })} /></div>
          <label className="block"><span className="text-[10px] uppercase tracking-widest text-neutral-500 block mb-1">Valore indicativo € (opz.)</span><input type="number" step="0.01" value={form.value_proposed} onChange={(e) => setForm({ ...form, value_proposed: e.target.value })} data-testid="cdl-value" className={inp} style={bs} /></label>
          <label className="block"><span className="text-[10px] uppercase tracking-widest text-neutral-500 block mb-1">Note</span><input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="cdl-notes" className={inp} style={bs} /></label>
          <div className="md:col-span-2 flex gap-2"><button type="submit" data-testid="cdl-save" className="px-4 py-2 rounded-md text-white text-sm" style={{ background: "var(--brand-primary)" }}>Crea trattativa</button><button type="button" onClick={() => setForm(null)} data-testid="cdl-cancel" className="px-4 py-2 rounded-md border text-sm" style={bs}>Annulla</button></div>
        </form>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div className="card-clean overflow-hidden" data-testid="crm-deals-list">
          {deals.length === 0 ? <div className="p-6 text-sm text-neutral-500">Nessuna trattativa. Usa "Nuova trattativa diretta" per un cliente gestito dall'amministratore.</div> : (
            <table className="w-full text-sm">
              <thead><tr className="text-left text-[10px] uppercase tracking-widest text-neutral-500 border-b" style={bs}><th className="px-4 py-3">Titolo</th><th className="px-4 py-3">Gestione</th><th className="px-4 py-3">Stato</th></tr></thead>
              <tbody>
                {deals.map((d) => (
                  <tr key={d.id} id={`open-${d.id}`} onClick={() => openDeal(d)} className={`border-b last:border-0 cursor-pointer hover:bg-neutral-50 ${openId === d.id || detail?.deal.id === d.id ? "bg-neutral-50" : ""}`} style={bs} data-testid={`crm-deal-row-${d.id}`}>
                    <td className="px-4 py-3 font-medium">{d.title}</td>
                    <td className="px-4 py-3 text-neutral-500">{agentName(d.agent_id)}</td>
                    <td className="px-4 py-3">{STAGE_LABEL[d.stage] || d.stage}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="card-clean p-5" data-testid="crm-deal-detail">
          {!detail ? <p className="text-sm text-neutral-500">{deals.length ? "Seleziona una trattativa." : "Nessuna trattativa da mostrare."}</p> : (
            <>
              <div className="font-serif text-xl mb-1">{detail.deal.title}</div>
              <div className="text-xs text-neutral-500 mb-4" data-testid="crm-deal-detail-stage">Stato: <b>{STAGE_LABEL[detail.deal.stage] || detail.deal.stage}</b> · {agentName(detail.deal.agent_id)}{detail.deal.loss_reason ? ` · Motivo: ${detail.deal.loss_reason}` : ""}</div>
              {!detail.deal.agent_id && (
                <div className="flex flex-wrap gap-2 mb-4 items-center">
                  <select value={detail.deal.stage} onChange={(e) => setStage(e.target.value)} data-testid="crm-deal-stage" className="px-2 py-1.5 rounded border text-xs" style={bs}>{Object.entries(STAGE_LABEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select>
                  <Link to={`/platform/crm/offerte?deal=${detail.deal.id}`} data-testid="crm-deal-to-offer" className="px-3 py-1.5 rounded-md text-white text-xs flex items-center gap-1" style={{ background: "var(--brand-dark)" }}><FileText className="w-3 h-3" /> Crea preventivo PDF</Link>
                </div>
              )}
              <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1">Attività</div>
              {detail.activities.length === 0 ? <p className="text-xs text-neutral-400">Nessuna attività.</p> : detail.activities.map((a) => (
                <div key={a.id} className="text-xs py-2 border-b last:border-0" style={bs}>
                  <b className="capitalize">{a.type}</b> {a.notes && `— ${a.notes}`} {a.done ? "✓" : ""} <span className="text-neutral-400">{formatDateTimeIT(a.created_at)}</span>
                </div>
              ))}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
